import Vue from 'vue';
import _ from 'lodash';
import BillyApi from '../../api/BillyApi';
import DDLogs from '../../DDLogs';
import Segment from '../../Segment';
import PaymeInterface from '../../Payme';
import SubscriptionStateApi from '../../api/SubscriptionStateApi';
import discountConsts from '../../constants/discount-benefit-bins';

const SUBSCRIPTION_STEPS = {
  creditCard: 'credit-card',
  done: 'done',
};

const CREDIT_CARD_FIELDS = {
  cardNumber: 'cardNumber',
  expiration: 'expiration',
  cvv: 'cvv',
  socialId: 'payerSocialId',
};

const PERSONAL_INFO_FIELDS = {
  firstName: 'payerFirstName',
  lastName: 'payerLastName',
  phoneNumber: 'payerPhone',
};

const state = {
  flowStep: SUBSCRIPTION_STEPS.creditCard,
  fieldsInstance: null,
  fields: null,
  firstBillingDate: null,
  firstBillingDateWithBenefit: null,
  personalInfo: {
    [PERSONAL_INFO_FIELDS.firstName]: { value: null, isValid: false },
    [PERSONAL_INFO_FIELDS.lastName]: { value: null, isValid: false },
    [PERSONAL_INFO_FIELDS.phoneNumber]: { value: null, isValid: false },
  },
  creditCard: {
    [CREDIT_CARD_FIELDS.cardNumber]: { isEmpty: true, isValid: false, error: '' },
    [CREDIT_CARD_FIELDS.expiration]: { isEmpty: true, isValid: false, error: '' },
    [CREDIT_CARD_FIELDS.cvv]: { isEmpty: true, isValid: false, error: '' },
    [CREDIT_CARD_FIELDS.socialId]: { isEmpty: true, isValid: false, error: '' },
  },
  tokenizationError: null,
  loadingToken: false,
  loadingBenefit: false,
  eligibleForDiscountBenefit: null,
  eligibilityError: null,
  paymeToken: null,
};

function trackValidationFailures(cardIsValid) {
  if (!cardIsValid) {
    _.chain(state.creditCard)
      .pickBy(value => !value.isValid)
      .keys()
      .forEach(field => Segment.trackUserGot('CreditCardValidationFailed', { field }))
      .value();
  }
}

function _isCardEligibleForDiscountBenefit(cardMask) {
  return _.some(discountConsts.DISCOUNT_BENEFIT_BINS, bin => _.startsWith(cardMask, bin));
}

const getters = {
  onCreditCardStep: state => state.flowStep === SUBSCRIPTION_STEPS.creditCard,
  onDone: state => state.flowStep === SUBSCRIPTION_STEPS.done,
  personalInfoFields: () => PERSONAL_INFO_FIELDS,
  creditCardFields: () => CREDIT_CARD_FIELDS,
  cardLast4Digits: state => _.takeRight(state.paymeToken && state.paymeToken.card.cardMask, 4).join(''),
};

const actions = {
  setPersonalInfo({ dispatch }, { firstName, lastName, phoneNumber }) {
    dispatch('setPersonalInfoField', { field: PERSONAL_INFO_FIELDS.firstName, value: firstName, isValid: true });
    dispatch('setPersonalInfoField', { field: PERSONAL_INFO_FIELDS.lastName, value: lastName, isValid: true });
    dispatch('setPersonalInfoField', { field: PERSONAL_INFO_FIELDS.phoneNumber, value: phoneNumber, isValid: true });
  },
  setPersonalInfoField({ commit }, { field, value, isValid, errorMessage }) {
    commit('setPersonalInfoField', { field, value, isValid, errorMessage });
  },
  setCreditCardField({ commit }, { field, isEmpty, isValid }) {
    commit('setCreditCardField', { field, isEmpty, isValid, error: '' });
  },
  setCreditCardValidations({ commit, state }) {
    if (!state.creditCard[CREDIT_CARD_FIELDS.cardNumber].isValid) {
      commit('setCreditCardValidationError', { field: CREDIT_CARD_FIELDS.cardNumber, error: 'אפשר לבדוק שוב את מספר הכרטיס?' });
    }
    if (!state.creditCard[CREDIT_CARD_FIELDS.expiration].isValid) {
      commit('setCreditCardValidationError', { field: CREDIT_CARD_FIELDS.expiration, error: 'התוקף לא נכון' });
    }
    if (!state.creditCard[CREDIT_CARD_FIELDS.cvv].isValid) {
      commit('setCreditCardValidationError', { field: CREDIT_CARD_FIELDS.cvv, error: '3 ספרות בגב הכרטיס' });
    }
    if (!state.creditCard[CREDIT_CARD_FIELDS.socialId].isValid) {
      commit('setPersonalInfoValidationError', { field: CREDIT_CARD_FIELDS.socialId, error: 'אפשר לבדוק שוב את מספר תעודת הזהות?' });
    }
    const cardIsValid = _.every(state.creditCard, field => field.isValid);
    trackValidationFailures(cardIsValid);
    return cardIsValid;
  },
  async init({ commit, dispatch }, { firstName, lastName, phoneNumber }) {
    const { fieldsInstance, fields } = await PaymeInterface.initOrGet();
    dispatch('setPersonalInfo', { firstName, lastName, phoneNumber });
    commit('setFieldsInstance', { fieldsInstance, fields });
    await dispatch('fetchFirstBillingDate');
  },
  async fetchFirstBillingDate({ commit }) {
    const { firstBillingDate } = await SubscriptionStateApi.fetchFirstBillingDate();
    commit('setFirstBillingDate', { firstBillingDate });
  },
  async createSubscription({ dispatch, commit }) {
    const creditCardValid = await dispatch('setCreditCardValidations');
    if (creditCardValid) {
      Segment.trackUserGot('SubscriptionSubmitSucceededOnValidation');
      try {
        commit('setLoadingToken', true);
        const paymeToken = state.paymeToken ? state.paymeToken : await dispatch('getPaymeToken');
        commit('setPaymeToken', paymeToken);
        Segment.trackUserGot('SubscriptionSubscribe');
        if (!this.eligibleForDiscountBenefit) {
          commit('setEligibleForDiscountBenefit', _isCardEligibleForDiscountBenefit(paymeToken.card.cardMask));
        }
        await BillyApi.createSubscription(paymeToken.token, paymeToken.card);
        Segment.trackUserGot('SubscriptionSubscribeSucceeded');
        commit('setStep', SUBSCRIPTION_STEPS.done);
      } catch (error) {
        DDLogs.error('Subscription failed', { errorMessage: error.message });
        Segment.trackUserGot('SubscriptionTokenizeError');
        commit('setTokenizationError', error);
      } finally {
        commit('setLoadingToken', false);
      }
    } else {
      Segment.trackUserGot('SubscriptionSubmitFailedOnValidation', { validations: state.creditCard });
    }
  },
  async updateCreditCard({ dispatch, commit }) {
    const creditCardValid = await dispatch('setCreditCardValidations');
    if (creditCardValid) {
      Segment.trackUserGot('UpdateCreditCardSucceededOnValidation');
      try {
        commit('setLoadingToken', true);
        const { token, card } = await dispatch('getPaymeToken');
        Segment.trackUserGot('UpdatingCreditCard');
        await BillyApi.updateCreditCard(token, card);
        Segment.trackUserGot('UpdatedCreditCardSucceeded');
        return true;
      } catch (error) {
        Segment.trackUserGot('UpdateCreditCardTokenizeError');
        commit('setTokenizationError', error);
        return false;
      } finally {
        commit('setLoadingToken', false);
      }
    } else {
      Segment.trackUserGot('UpdatedCreditCardFailedOnValidation', { validations: state.creditCard });
      return false;
    }
  },
  async checkDiscountBenefitEligibility({ dispatch, commit }) {
    const creditCardValid = await dispatch('setCreditCardValidations');
    if (creditCardValid) {
      Segment.trackUserGot('CheckDiscountBenefitSucceededOnValidation');
      try {
        commit('setLoadingBenefit', true);
        const paymeToken = await dispatch('getPaymeToken');
        commit('setPaymeToken', paymeToken);
        commit('setEligibleForDiscountBenefit', _isCardEligibleForDiscountBenefit(paymeToken.card.cardMask));
        return true;
      } catch (error) {
        Segment.trackUserGot('CheckDiscountBenefitTokenizeError');
        commit('setEligibilityError', error);
        return false;
      } finally {
        commit('setLoadingBenefit', false);
      }
    } else {
      Segment.trackUserGot('CheckDiscountBenefitFailedOnValidation', { validations: state.creditCard });
      return false;
    }
  },
  async getPaymeToken({ state, rootState }) {
    const { customerId } = rootState.session;
    const sale = {
      payerFirstName: state.personalInfo[PERSONAL_INFO_FIELDS.firstName].value,
      payerLastName: state.personalInfo[PERSONAL_INFO_FIELDS.lastName].value,
      payerPhone: state.personalInfo[PERSONAL_INFO_FIELDS.phoneNumber].value.replace('-', ''),
      payerEmail: `${customerId}@riseup.co.il`,
      total: {
        label: 'תשלום לרייזאפ',
        amount: {
          currency: 'ILS',
          value: '45.00',
        },
      },
    };
    Segment.trackUserGot('SubscriptionTokenize');
    const tokenRequest = await state.fieldsInstance.tokenize(sale);
    Segment.trackUserGot('SubscriptionTokenizeSucceeded');
    const { token, card } = tokenRequest;
    return { token, card };
  },
  reset({ commit }) {
    commit('setTokenizationError', null);
  },
};

const mutations = {
  setStep(state, flowStep) {
    state.flowStep = flowStep;
  },
  setPaymeToken(state, paymeToken) {
    state.paymeToken = paymeToken;
  },
  setFieldsInstance(state, { fieldsInstance, fields }) {
    state.fieldsInstance = fieldsInstance;
    state.fields = fields;
  },
  setFirstBillingDate(state, { firstBillingDate }) {
    // eslint-disable-next-line no-param-reassign
    state.firstBillingDate = firstBillingDate;
  },
  setPersonalInfoField(state, { field, value, isValid }) {
    Vue.set(state.personalInfo, field, { value, isValid });
  },
  setCreditCardField(state, { field, isEmpty, isValid, error }) {
    Vue.set(state.creditCard, field, { isEmpty, isValid, error });
  },
  setTokenizationError(state, error) {
    state.tokenizationError = error;
  },
  setEligibilityError(state, error) {
    state.eligibilityError = error;
  },
  setPersonalInfoValidationError(state, { field, error }) {
    Vue.set(state.personalInfo, field, _.extend({}, { error }, state.personalInfo[field]));
  },
  setCreditCardValidationError(state, { field, error }) {
    Vue.set(state.creditCard, field, _.extend({}, state.creditCard[field], { error }));
  },
  setLoadingToken(state, loading) {
    state.loadingToken = loading;
  },
  setLoadingBenefit(state, loading) {
    state.loadingBenefit = loading;
  },
  setEligibleForDiscountBenefit(state, value) {
    state.eligibleForDiscountBenefit = value;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
